import type { DateFormat, FileUploadType, QuestionValue, ResidenceType } from '@webapp/account/src/resources/questions';

import type { SmilePackName } from '@webapp/common/components/smile-image';
import type { DateTimeType, TimeFormat } from '@webapp/common/lib/date';
import { getColor } from '@webapp/common/lib/ui';
import { CANTRATE_TEXT } from '@webapp/common/resources/constants';
import type { ConjointConfig, IQuestionCategory } from '@webapp/common/resources/entities/question';
import { DropdownType } from '@webapp/common/resources/survey';

export interface Question {
    answers;
    groups;
    categories?: Array<IQuestionCategory>;
    ID: number;
    logics;
    forwardLogic;
    PAGE: number;
    parameters: string;
    QUESTION: number;
    QUESTION_TYPE_ID: number;
    MAXDIFF_BLOCK_COUNT?: number;
    TARGET_REPETITIONS_OPTIONS?: Array<{ value: number; isIdeal: boolean }>;
    SERIAL: number;
    FILE_ID?: number;
    ORDER: number;
    FILE_PATH?: string;
    PINNED?: number;
    conjointConfig?: ConjointConfig;
}

export interface QuestionValues {
    value: QuestionValue;
    value2?: QuestionValue;
    value3?: QuestionValue;
}

export interface NestedItem {
    id: number;
    name: string;
    parentId: number;
    level: number;
    answerId: number;
}

export enum QuestionParamsModelPairComparisonType {
    TEXT = 'text',
    IMAGE = 'image',
}

export enum QuestionParamsCardSortingType {
    CLOSE = 'close',
    OPEN = 'open',
}

export enum QuestionParamsModelAbType {
    TEXT = 'text',
    VIDEO = 'video',
    IMAGE = 'image',
}

export type PerceptionTextItem = {
    word: string;
    pos: number;
    isTag?: boolean;
    isSelectable?: boolean;
};

export interface QuestionParams {
    ABType?: QuestionParamsModelAbType;
    additionalImage?: string;
    additionalVideo?: string;
    additionalAudio?: string;
    additionalText?: string;
    agreementLeftLabel: string;
    agreementLink: string;
    agreementRightLabel: string;
    agreementText: string;
    amount: number;
    answerType: 'any' | 'number';
    answers: Array<string>;
    cantRate?: boolean;
    cantRateText?: string;
    chartColor?: string;
    chartType?: string;
    color: string;
    comment: boolean;
    commentRequired: boolean;
    commentText: string;
    csiFormat: string;
    dateFormat: DateFormat;
    dateType: DateTimeType;
    distributeLabel: string;
    distributeScore: number;
    distributeSurplus: boolean;
    distributeUnit: string;
    dropdownListType: DropdownType;
    dropdownType: 'single' | 'multi';
    excludeSelectedAnswer: boolean;
    fileAmount: number;
    fileLimitExt: Array<string>;
    fileType: FileUploadType;
    help: boolean;
    helpText: string;
    hours: number;
    imageColumns: number;
    imageTextPosition: 'under' | 'above';
    matrixNumbersColor: Array<string>;
    matrixType: 'stars' | 'numbers' | 'numbers-with-borders' | 'slider';
    // Кано: кастомные тексты оценок (общие для всех функций вопроса)
    kanoAnswers?: Array<string>;
    maxAmount: number;
    maxLength?: number;
    minAmount: number;
    minLength?: number;
    minutes: number;
    name: boolean; // show name. TODO ?? purge
    npsColors: Array<string>;
    npsLeftLabel: string;
    npsRightLabel: string;
    npsType: 'numbers' | 'numbers-with-borders' | 'slider';
    phoneFormatCountry: string;
    phoneFormatNumber: string;
    placeholder?: string;
    questionColumns: number;
    randomOrder: boolean;
    colsRandomOrder: boolean;
    randomOrderText?: string;
    randomCategoriesOrder?: boolean;
    randomScreensOrder?: boolean;
    required: boolean;
    residence: ResidenceType;
    scaleColors?: Array<string>;
    scaleEndDia?: number;
    scaleFormat?: 'numbers' | 'numbers-with-borders' | 'slider';
    scaleStartDia?: number;
    scaleType?: 'number' | 'custom';
    seconds: number;
    size: number;
    smilePack: SmilePackName;
    smileType: string;
    testScore?: number;
    textBlock: string;
    timeFormat: TimeFormat;
    timer: boolean;
    totalScore?: boolean;
    uploadButtonName: string;
    questionText?: string;
    perceptionText?: Array<PerceptionTextItem>;
    pairComparisonType?: QuestionParamsModelPairComparisonType;
    clickCount?: number;
    cardSortingType?: QuestionParamsCardSortingType;
    blockSize?: number;
    bestLabel?: string;
    worstLabel?: string;
    fiveSecDuration?: number;
    firstClickView?: 'by-width' | 'by-height';
    targetRepetitions?: number;
    carryMediaForwardFromId?: number;
    imagePosition?: string;
    figmaImportCode?: string;
    figmaImportedAt?: string;
    figmaTargetScreens?: string;
    figmaDisplayMode?: 'fitWidth' | 'original' | 'fitHeight';
    figmaTimerSeconds?: number | null;
    figmaRecordScreen?: boolean;
    figmaRecordAudio?: boolean;
    figmaRecordCamera?: boolean;
    randomCardsCount?: number;
    testAnswerReaction?: boolean;
    testAnswerCorrectText?: string;
    testAnswerIncorrectText?: string;
}

export const initialQuestionParams: Readonly<QuestionParams> = {
    additionalText: '',
    agreementLeftLabel: null,
    agreementLink: null,
    agreementRightLabel: null,
    agreementText: null,
    amount: null,
    answerType: null,
    answers: [],
    cantRate: false,
    cantRateText: CANTRATE_TEXT,
    color: null,
    comment: false,
    commentRequired: false,
    commentText: '',
    csiFormat: 'numbers',
    dateFormat: null,
    dateType: null,
    distributeLabel: null,
    distributeScore: null,
    distributeSurplus: null,
    distributeUnit: null,
    dropdownListType: DropdownType.DIFFERENT,
    dropdownType: 'single',
    excludeSelectedAnswer: false,
    fileAmount: null,
    fileLimitExt: [],
    fileType: null,
    help: false,
    helpText: null,
    hours: 0,
    imageColumns: 1,
    imageTextPosition: 'under',
    matrixNumbersColor: [getColor('blue-nps')],
    matrixType: 'stars',
    maxAmount: 10,
    maxLength: null,
    minAmount: 1,
    minLength: null,
    minutes: 1,
    name: true,
    npsColors: [getColor('blue-nps')],
    npsLeftLabel: null,
    npsRightLabel: null,
    npsType: 'numbers',
    phoneFormatCountry: null,
    phoneFormatNumber: null,
    questionColumns: 1,
    randomOrder: false,
    colsRandomOrder: false,
    required: true,
    residence: null,
    scaleColors: [getColor('blue-nps')],
    scaleEndDia: 5,
    scaleFormat: 'numbers',
    scaleStartDia: 1,
    scaleType: 'number',
    seconds: 0,
    size: null,
    smilePack: null,
    smileType: null,
    textBlock: null,
    timeFormat: null,
    timer: false,
    uploadButtonName: null,
    figmaRecordScreen: false,
};
